const CURRENCY = "USD";
const LOCALE = "en-US";

const currencyFormatter = new Intl.NumberFormat(LOCALE, {
  style: "currency",
  currency: CURRENCY,
  maximumFractionDigits: 2,
});

const currencySymbol =
  new Intl.NumberFormat(LOCALE, { style: "currency", currency: CURRENCY })
    .formatToParts(0)
    .find((p) => p.type === "currency")?.value ?? "$";

const relativeTimeFormatter = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

const dateTimeFormatter = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

const dateFormatter = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
});

export function formatCurrency(value: number): string {
  return currencyFormatter.format(value);
}

export function formatCurrencyCompact(value: number): string {
  if (!Number.isFinite(value)) return formatCurrency(value);

  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";

  let scale: number;
  let suffix: string;
  let fractionDigits: number;

  if (abs >= 1_000_000_000) {
    scale = 1_000_000_000;
    suffix = "B";
    fractionDigits = 2;
  } else if (abs >= 1_000_000) {
    scale = 1_000_000;
    suffix = "M";
    fractionDigits = 2;
  } else if (abs >= 1_000) {
    scale = 1_000;
    suffix = "K";
    fractionDigits = abs >= 100_000 ? 0 : 1;
  } else {
    scale = 1;
    suffix = "";
    fractionDigits = 0;
  }

  const scaled = abs / scale;
  const factor = 10 ** fractionDigits;
  const rounded = Math.floor(scaled * factor + 0.5) / factor;

  const numberPart = rounded.toLocaleString(LOCALE, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });

  return `${sign}${currencySymbol}${numberPart}${suffix}`;
}

export function formatPercent(value: number, digits = 1): string {
  return `${value.toFixed(digits)}%`;
}

type Unit = Intl.RelativeTimeFormatUnit;

const UNITS: Array<[Unit, number]> = [
  ["year", 60 * 60 * 24 * 365],
  ["month", 60 * 60 * 24 * 30],
  ["week", 60 * 60 * 24 * 7],
  ["day", 60 * 60 * 24],
  ["hour", 60 * 60],
  ["minute", 60],
  ["second", 1],
];

export function formatRelativeTime(iso: string, now: Date = new Date()): string {
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return "—";
  const deltaSec = Math.round((ts - now.getTime()) / 1000);
  const abs = Math.abs(deltaSec);
  if (abs < 30) return "just now";
  for (const [unit, sec] of UNITS) {
    if (abs >= sec || unit === "second") {
      const value = Math.round(deltaSec / sec);
      return relativeTimeFormatter.format(value, unit);
    }
  }
  return relativeTimeFormatter.format(deltaSec, "second");
}

export function formatAbsoluteDateTime(iso: string): string {
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return "—";
  return dateTimeFormatter.format(new Date(ts));
}

export function formatAbsoluteDate(iso: string): string {
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return "—";
  return dateFormatter.format(new Date(ts));
}
